import React, { useEffect, useMemo, useRef, useState, } from 'react';
import * as Styled from './ColorPicker.styles';
import { useFormikContext } from 'formik';
import get from 'lodash/get';
import { Popper, Grow, ClickAwayListener, TextField, InputAdornment, IconButton, } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button } from '@innovyze/stylovyze/lib/components';
import { palette } from '@innovyze/stylovyze/lib/utils/styles';
import { RgbaColorPicker } from 'react-colorful';
export const defaultColorOptions = [
    palette.primary.main,
    palette.secondary.main,
    palette.error.main,
    palette.warning.main,
    '#83BC3F',
    '#886CE7',
    palette.primary.dark,
    palette.secondary.dark,
    palette.error.dark,
    palette.warning.dark,
    '#4B7E04',
    '#4C3DAD',
    palette.primary.light,
    palette.secondary.light,
    palette.error.light,
    palette.warning.light,
    '#B6EF70',
    '#BAA6FF',
];
export const componentToHex = (c) => {
    const hex = c.toString(16);
    return hex.length == 1 ? '0' + hex : hex;
};
export const rgbaToHex = (color) => {
    return ('#' +
        componentToHex(color.r) +
        componentToHex(color.g) +
        componentToHex(color.b));
};
export const hexToRgba = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
            a: 1,
        }
        : { r: 0, g: 0, b: 0, a: 1 };
};
const rgbaToString = (rgba) => `rgba(${rgba.r},${rgba.g},${rgba.b},${rgba.a})`;
export const stringToRgba = (str) => {
    const rgba = str.replace(/[^\d,]/g, '').split(',');
    return rgba
        ? {
            r: parseInt(rgba[0], 16),
            g: parseInt(rgba[1], 16),
            b: parseInt(rgba[2], 16),
            a: parseInt(rgba[3], 16),
        }
        : { r: 0, g: 0, b: 0, a: 1 };
};
export const ColorPicker = ({ name, zIndex, label, value, options = defaultColorOptions.map(c => rgbaToString(hexToRgba(c))), onClick, onClickAway, onChange, placement, dataCy, submitOnChange = false, variant = 'textField', TextFieldProps, children, }) => {
    const { values, touched, setFieldValue, setFieldTouched, submitForm } = useFormikContext();
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedColor, setSelectedColor] = useState(() => {
        const initialValue = value ? value : get(values, name);
        return initialValue?.startsWith('#')
            ? rgbaToString(hexToRgba(initialValue))
            : initialValue;
    });
    const [customColor, setCustomColor] = useState({
        rgba: {
            r: 0,
            g: 0,
            b: 0,
            a: 1,
        },
        hex: '#000000',
    });
    const [customColors, setCustomColors] = useState([]);
    const [mode, setMode] = useState('pickColor');
    const hasBeenTouched = useMemo(() => {
        return get(touched, name);
    }, [touched, name]);
    const colorFormValue = useMemo(() => {
        return get(values, name);
    }, [values, name]);
    const handleSelect = (newColor) => {
        setFieldValue(name, newColor);
        setFieldTouched(name, true, false);
        setSelectedColor(newColor);
        setIsOpen(false);
        onChange?.(newColor);
    };
    const handleClickInput = () => {
        setIsOpen(!isOpen);
        onClick?.();
    };
    const handleClickAway = (e) => {
        onClickAway?.();
        if (anchorRef.current &&
            anchorRef.current.contains(e.target)) {
            return;
        }
        setIsOpen(false);
    };
    const addCustomColor = () => {
        setCustomColors([...customColors, rgbaToString(customColor.rgba)]);
        setMode('pickColor');
    };
    const changeRgbaCustomColor = (key) => (e) => {
        let inputValue = e.target.value ? parseInt(e.target.value) : 0;
        if (key !== 'a' && (inputValue < 0 || inputValue > 255)) {
            //if not a valid element for rgb
            inputValue = 0;
        }
        if (key === 'a' && (inputValue <= 0 || inputValue > 100)) {
            //if not a valid element for a
            inputValue = 100;
        }
        setCustomColor({
            hex: rgbaToHex({
                ...customColor.rgba,
                [key]: key !== 'a' ? inputValue : inputValue / 100,
            }),
            rgba: {
                ...customColor.rgba,
                [key]: key !== 'a' ? inputValue : inputValue / 100,
            },
        });
    };
    const changeHexCustomColor = (e) => {
        const hexValue = e.target.value ? e.target.value : '#000000';
        setCustomColor({
            rgba: hexToRgba(hexValue),
            hex: hexValue,
        });
    };
    useEffect(() => {
        if (value) {
            handleSelect(value);
        }
    }, [value]);
    useEffect(() => {
        if (submitOnChange && hasBeenTouched) {
            submitForm();
        }
    }, [submitOnChange, name, hasBeenTouched, colorFormValue]);
    return (React.createElement(Styled.ColorPicker, { ref: anchorRef, "data-testid": "ColorPicker" },
        React.createElement("div", { "aria-hidden": "true", onClick: handleClickInput, "data-cy": dataCy },
            children !== undefined && children,
            !children && variant === 'textField' && (React.createElement(TextField, { variant: "outlined", label: label, margin: "normal", inputProps: { contentEditable: false, disabled: true }, value: selectedColor, InputProps: {
                    endAdornment: (React.createElement(InputAdornment, { position: "end" },
                        React.createElement(Styled.SelectedColor, { color: selectedColor }))),
                }, ...TextFieldProps })),
            !children && variant === 'circle' && (React.createElement(Styled.ColorOption, { "$option": selectedColor }))),
        React.createElement(Popper, { style: { zIndex: zIndex || 950 }, open: isOpen, anchorEl: anchorRef.current, placement: placement }, () => (React.createElement(Grow, null,
            React.createElement(ClickAwayListener, { onClickAway: handleClickAway },
                React.createElement("div", null,
                    mode === 'pickColor' && (React.createElement(Styled.ColorPickerPaper, null,
                        React.createElement(Styled.ColorGrid, null, options?.map(option => (React.createElement(Styled.ColorOption, { "$option": option, key: typeof option ===
                                'string'
                                ? option
                                : option.value, onClick: () => handleSelect(typeof option ===
                                'string'
                                ? option
                                : option.value) })))),
                        React.createElement(Styled.ColorDivider, null),
                        React.createElement(Styled.ColorPickerLabel, null, "Custom"),
                        React.createElement(Styled.ColorGrid, null,
                            customColors?.map(customOption => (React.createElement(Styled.ColorOption, { "$option": customOption, key: customOption, onClick: () => handleSelect(customOption) }))),
                            React.createElement(IconButton, { color: "secondary", onClick: () => setMode('addCustom'), size: "large" },
                                React.createElement(AddCircleIcon, null))))),
                    mode === 'addCustom' && (React.createElement(Styled.ColorPickerPaper, null,
                        React.createElement(Styled.ButtonContainer, null,
                            React.createElement(Button, { color: "secondary", onClick: () => setMode('pickColor') }, "Cancel"),
                            React.createElement(Button, { color: "primary", onClick: addCustomColor }, "OK")),
                        React.createElement(Styled.HexColorPickerWrapper, null,
                            React.createElement(RgbaColorPicker, { color: customColor.rgba, onChange: newColor => setCustomColor({
                                    rgba: newColor,
                                    hex: rgbaToHex(newColor),
                                }) })),
                        React.createElement(Styled.InputGrid, null,
                            React.createElement(Styled.TexFieldGrid, { variant: "outlined", size: "small", value: customColor.hex, type: "hex", onChange: changeHexCustomColor, helperText: "HEX" }),
                            React.createElement(Styled.TexFieldGrid, { variant: "outlined", size: "small", onChange: changeRgbaCustomColor('r'), value: customColor.rgba.r, type: "rgb", helperText: "R" }),
                            React.createElement(Styled.TexFieldGrid, { variant: "outlined", size: "small", onChange: changeRgbaCustomColor('g'), value: customColor.rgba.g, type: "rgb", helperText: "G" }),
                            React.createElement(Styled.TexFieldGrid, { variant: "outlined", size: "small", onChange: changeRgbaCustomColor('b'), value: customColor.rgba.b, type: "rgb", helperText: "B" }),
                            React.createElement(Styled.TexFieldGrid, { variant: "outlined", size: "small", value: customColor.rgba.a * 100, onChange: changeRgbaCustomColor('a'), type: "rgb", helperText: "A" })))))))))));
};
export default ColorPicker;
